import { FC, useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import Select, { Option } from '@/components/elements/Form/Select';
import Loading from '@/components/elements/Icon/Loading';
import { SearchEntryRacerResult } from '@/lib/apollo/types/gen/graphql';
import { defaultTheme } from '@/utility/theme';
import { toHalfWidth } from '@/utility/utils';

import {
  Container,
  DetailContainer,
  DetailSelectors,
  DetailWrapper,
  EmptyMessage,
  LinkList,
  ListItem,
  LoadingWrapper,
  SelectArea,
  SortSelector,
} from '../style';

interface SearchRacerResultTemplateProps {
  loading: boolean;
  searchResults: SearchEntryRacerResult[];
  defaultSort?: string;
  clickRacerName: (val: string) => void;
}

const SearchRacerResultTemplate: FC<SearchRacerResultTemplateProps> = ({
  loading,
  searchResults,
  defaultSort,
  clickRacerName,
}) => {
  if (loading) {
    return (
      <LoadingWrapper>
        <Loading
          width="48px"
          height="48px"
          color={defaultTheme.border.color.darkGray}
        />
      </LoadingWrapper>
    );
  }

  if (!searchResults) {
    return <></>;
  }

  const [sortItem, setSortItem] = useState<string>('averageScore');
  const sortOptions = useMemo(() => {
    const options: Option[] = [];
    options.push({ value: 'averageScore', displayName: '競走得点' });
    options.push({ value: 'age', displayName: '年齢' });
    options.push({ value: 'graduated', displayName: '卒業期' });
    return options;
  }, []);

  const sortedResults = useMemo(() => {
    let result: SearchEntryRacerResult[] = [];
    if (searchResults && searchResults.length != 0) {
      result = searchResults
        .filter(res => res.name != '')
        .sort((a, b) => {
          if (sortItem === 'age') {
            return b.age - a.age; // 「b-a」降順
          } else if (sortItem === 'graduated') {
            return b.intGraduated - a.intGraduated; // 「b-a」降順
          }
          return b.averageRaceScore - a.averageRaceScore;
        });
    }
    return result;
  }, [searchResults, sortItem]);

  useLayoutEffect(() => {
    if (defaultSort) {
      setSortItem(defaultSort);
    }
  }, [defaultSort]);

  return (
    <Container>
      <DetailWrapper>
        <DetailContainer>
          <SelectArea>
            <DetailSelectors>
              <span>検索結果数： {searchResults.length} 名</span>
              <SortSelector>
                <span>ソート項目</span>
                <Select
                  radius="s"
                  options={sortOptions}
                  value={sortItem}
                  width="110px"
                  onChange={e => setSortItem(e.target.value)}
                />
              </SortSelector>
            </DetailSelectors>
          </SelectArea>
          <LinkList>
            {loading && (
              <LoadingWrapper>
                <Loading
                  width="48px"
                  height="48px"
                  color={defaultTheme.border.color.darkGray}
                />
              </LoadingWrapper>
            )}
            {sortedResults.length ? (
              <>
                {sortedResults.map((res, idx) => (
                  <Link
                    key={idx}
                    onClick={() => {
                      clickRacerName(sortItem);
                    }}
                    to={`/racer/detail?registrationNum=${res.registrationNum}`}
                  >
                    <ListItem>
                      {`${res.name}（${res.age}）${res.prefecture} ${
                        res.graduated
                      }期 ${toHalfWidth(res.gradeName)}級${toHalfWidth(
                        res.unitName,
                      )}班 ${res.averageRaceScore.toFixed(2)}`}
                    </ListItem>
                  </Link>
                ))}
              </>
            ) : (
              <EmptyMessage>
                検索条件に一致する選手情報はありません
              </EmptyMessage>
            )}
          </LinkList>
        </DetailContainer>
      </DetailWrapper>
    </Container>
  );
};

export default SearchRacerResultTemplate;
